import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Loader2, Save, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EntitySelect } from '@/components/shared/EntitySelect';
import { SharedCombobox } from '@/components/shared/SharedCombobox';
import { StandardDetailLayout } from '@/layouts/StandardDetailLayout';

export interface DesignFormData {
  productId: string;
  designType: 'NEW' | 'EDIT';
  quantity: number;
  description: string;
  status?: 'PENDING' | 'DESIGNING' | 'REVIEWING' | 'APPROVED' | 'REJECTED';
  assignedTo?: string;
  filePaths?: string;
}

interface DesignFormProps {
  initialData?: Partial<DesignFormData> & { id?: string };
  onSubmit: (data: DesignFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  mode: 'create' | 'edit';
  isModal?: boolean;
}

export function DesignForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  mode,
  isModal = false,
}: DesignFormProps) {
  const { control, handleSubmit, register } = useForm<DesignFormData>({
    defaultValues: {
      productId: initialData?.productId || '',
      designType: (initialData?.designType as any) || 'NEW',
      quantity: initialData?.quantity || 1,
      description: initialData?.description || '',
      status: (initialData?.status as any) || 'PENDING',
      assignedTo: initialData?.assignedTo || '',
      filePaths: typeof initialData?.filePaths === 'string' ? initialData.filePaths : (initialData?.filePaths ? JSON.stringify(initialData.filePaths) : ''),
    },
  });


  const formContent = (
    <form id="design-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-xs space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-800">
            {mode === 'create' ? 'Thông tin phiếu yêu cầu thiết kế' : 'Cập nhật hồ sơ thiết kế'}
          </h3>
        </div>

        {/* Sản phẩm liên kết */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-700">
            Sản phẩm <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="productId"
            control={control}
            rules={{ required: 'Vui lòng chọn sản phẩm' }}
            render={({ field, fieldState }) => (
              <>
                <EntitySelect
                  entityType="products"
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  placeholder="Tìm kiếm và chọn sản phẩm..."
                />
                {fieldState.error && (
                  <span className="text-xs text-red-500 mt-1 block">
                    {fieldState.error.message}
                  </span>
                )}
              </>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Loại thiết kế */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Phân loại thiết kế</Label>
            <Controller
              name="designType"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Chọn loại thiết kế..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEW" className="text-xs">Thiết kế mới hoàn toàn</SelectItem>
                    <SelectItem value="EDIT" className="text-xs">Chỉnh sửa file cũ / Thay đổi nội dung</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Số lượng mẫu yêu cầu */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Số lượng mẫu in thử (Mockup)</Label>
            <Input
              type="number"
              min={1}
              className="h-9 text-xs"
              {...register('quantity', { valueAsNumber: true })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Người phụ trách thiết kế */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Kỹ thuật viên phụ trách</Label>
            <Controller
              name="assignedTo"
              control={control}
              render={({ field }) => (
                // Chỉ nhân sự tổ Thiết kế / Bình file / Chế bản (CTP) — quan hệ phụ thuộc quy trình thiết kế (03/11/2026); để trống = chưa phân công
                <SharedCombobox
                  type="user"
                  params={{ processGroup: 'PREPRESS' }}
                  value={field.value || ''}
                  onChange={(v) => field.onChange(v || '')}
                  placeholder="Chưa phân công..."
                />
              )}
            />
          </div>

          {/* Trạng thái */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Trạng thái hồ sơ</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Chọn trạng thái..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING" className="text-xs">Chờ xử lý</SelectItem>
                    <SelectItem value="DESIGNING" className="text-xs">Đang thiết kế</SelectItem>
                    <SelectItem value="REVIEWING" className="text-xs">Chờ duyệt KCS</SelectItem>
                    <SelectItem value="APPROVED" className="text-xs">Đã duyệt</SelectItem>
                    <SelectItem value="REJECTED" className="text-xs">Từ chối / Sửa lại</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Link file lưu trữ */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-700">Đường dẫn tệp Artwork / AI / PDF / Drive</Label>
          <Input
            placeholder="https://drive.google.com/... hoặc đường dẫn máy chủ file"
            className="h-9 text-xs"
            {...register('filePaths')}
          />
        </div>

        {/* Mô tả / Ghi chú */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-700">Yêu cầu chi tiết & Quy cách thiết kế</Label>
          <Textarea
            rows={4}
            placeholder="Nhập các yêu cầu về màu sắc, đường cấn, bleed, ép nhũ, UV, lưu ý khi bình trang..."
            className="text-xs leading-relaxed"
            {...register('description')}
          />
        </div>
      </div>

      {/* Nút submit dưới chân form (Dual-position Form Submit - Rule 3.2) */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
          className="h-8 text-xs cursor-pointer"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Hủy
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={isSubmitting}
          className="h-8 text-xs bg-primary text-primary-foreground font-medium cursor-pointer shadow-xs"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              Đang lưu...
            </>
          ) : (
            <>
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {mode === 'create' ? 'Tạo yêu cầu' : 'Lưu thay đổi'}
            </>
          )}
        </Button>
      </div>
    </form>
  );

  if (isModal) {
    return formContent;
  }

  return (
    <StandardDetailLayout
      title={mode === 'create' ? 'Tạo Yêu Cầu Thiết Kế' : 'Chỉnh Sửa Hồ Sơ Thiết Kế'}
      breadcrumbs={[{ label: "Thiết kế", to: "/designs" }]}
      actions={
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
            className="h-7 text-xs"
          >
            Hủy
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isSubmitting}
            onClick={() => {
              const submitBtn = document.querySelector('#design-form button[type="submit"]') as HTMLButtonElement;
              submitBtn?.click();
            }}
            className="h-7 text-xs bg-primary text-primary-foreground font-medium"
          >
            {isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            {mode === 'create' ? 'Tạo mới' : 'Lưu'}
          </Button>
        </div>
      }
    >
      <div className="max-w-4xl mx-auto p-4">
        {formContent}
      </div>
    </StandardDetailLayout>
  );
}
